import React, { useMemo, useState } from "react";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import { EXTERNAL_2D_ENABLED, SERVER_BASE_URL } from "../../config";
import { processModelFile, verifyResourceExists } from "../../services/modelService";
import MultipleModelViewer from "../MultipleModelViewer/MultipleModelViewer";
import ModelIcon from "../ModelIcon/ModelIcon";
import ModelPDFIcon from "../ModelPDFIcon/ModelPDFIcon";
import {
	extractFilename,
	selectBestDirectModelFromModelData,
	countAvailableModelsFromModelData,
	hasDirectMachineModelsFromModelData,
} from "../../utilities/selectBestModel";
import { trackEvent } from "../../utilities/analyticsService";

const ModelButton = ({
	machineName,
	machineNumber,
	machineProductName,
	machineid,
	productNumbers,
	modelData = {},
	forcedModel = null,
	analyticsContext = {},
}) => {
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);

	const { user } = useAuth();
	const { selectedSite, selectedSiteCurrency, selectedSiteData } = useSite();
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const returnPath = useMemo(
		() => `${location.pathname}${location.search}${location.hash}`,
		[location.pathname, location.search, location.hash]
	);

	const bestModel = useMemo(() => {
		if (forcedModel) {
			return {
				type: forcedModel.modelType,
				format: forcedModel.format,
				engine: forcedModel.engine,
				path: forcedModel.filepath.replace(SERVER_BASE_URL, ""),
				filename: forcedModel.filename,
			};
		}
		return selectBestDirectModelFromModelData(modelData);
	}, [forcedModel, modelData]);

	const availableModelCount = useMemo(() => {
		if (forcedModel) return 1;
		return countAvailableModelsFromModelData(modelData);
	}, [forcedModel, modelData]);

	const shouldShowMultipleSelector = !forcedModel && availableModelCount > 1;

	const showError = () =>
		Swal.fire(t('ALERT_MESSAGES.ERROR'), t('GENERAL.FAILED_TO_OPEN_MODEL'), "error");

	const openUrlIfExists = async (url) => {
		if (!url) return false;
		const ok = await verifyResourceExists(url);
		if (!ok) return false;
		window.open(url);
		return true;
	};

	const build2dUrl = (pathValue) => {
		if (!pathValue) return "";
		let normalized = String(pathValue).trim();
		if (!normalized) return "";
		if (/^https?:\/\//i.test(normalized)) return normalized;
		normalized = normalized.replace(/\\/g, "/");
		normalized = normalized.replace(/^https?:\/\/[^/]+/i, "");
		normalized = normalized.replace(/^\/?stock-models\//i, "");
		normalized = normalized.replace(/^\/?models\//i, "");
		normalized = normalized.replace(/^\/?modelfiles\//i, "");
		normalized = normalized.replace(/^\/+/i, "");
		if (!/^2d\//i.test(normalized)) {
			normalized = `2D/${normalized}`;
		}
		if (!/\.pdf$/i.test(normalized)) {
			normalized = `${normalized}.pdf`;
		}
		return `${SERVER_BASE_URL}/stock-models/${normalized}`;
	};

	const buildModelAnalyticsPayload = (type, filename, extra = {}) => ({
		...analyticsContext,

		page:
			type === "2D"
				? `/2d-model-viewer/${machineid}/${filename}`
				: `/model-viewer/${machineid}/${filename}`,

		page_label: `${machineNumber || machineid || ""} | ${type} Model - ${filename}`,

		machine_id: machineid || "",
		machine_number: machineNumber || "",
		machine_name: machineName || "",
		machine_product_name: machineProductName || "",

		model_name: filename || "",
		file_name: filename || "",
		model_type: type,

		customer_id: analyticsContext.customer_id || selectedSiteData?.cus_id || "",
		customer_name:
			analyticsContext.customer_name ||
			selectedSiteData?.parent_customer_name ||
			selectedSiteData?.customer_name ||
			"",
		customer_number:
			analyticsContext.customer_number ||
			selectedSiteData?.customer_number ||
			"",

		site_id: analyticsContext.site_id || selectedSiteData?.site_id || "",
		site_name:
			analyticsContext.site_name ||
			selectedSiteData?.customer_site_name ||
			selectedSiteData?.site_name ||
			selectedSite ||
			"",
		site_number:
			analyticsContext.site_number ||
			selectedSiteData?.customer_site_number ||
			selectedSite ||
			"",
		customer_site_number:
			analyticsContext.customer_site_number ||
			selectedSiteData?.customer_site_number ||
			selectedSite ||
			"",

		legal_entity:
			analyticsContext.legal_entity ||
			selectedSiteData?.site_legal_entity ||
			selectedSiteData?.legal_entity ||
			"",

		source: "model_button",
		...extra,
	});
	const handleOpen = async () => {
		if (!bestModel) return showError();

		try {
			const filename = extractFilename(bestModel.path);

			if (bestModel.type === "3D" && bestModel.format === "glb") {
				trackEvent(
					"model_3d_opened",
					buildModelAnalyticsPayload("3D", filename, {
						model_format: "glb",
					})
				);

				navigate(`/model-viewer/${machineid}/${filename}`, {
					state: { fromPath: returnPath, analyticsTracked3dOpen: true },
				});
				return;
			}

			if (bestModel.type === "2D") {
				trackEvent(
					"model_2d_opened",
					buildModelAnalyticsPayload("2D", filename, {
						model_format: "pdf",
						external_2d_enabled: EXTERNAL_2D_ENABLED,
					})
				);

				if (EXTERNAL_2D_ENABLED) {
					const pdfUrl = build2dUrl(bestModel.path);
					if (await openUrlIfExists(pdfUrl)) return;
					showError();
					return;
				}

				navigate(`/2d-model-viewer/${machineid}/${filename}`, {
					state: { fromPath: returnPath },
				});
				return;
			}

			if (bestModel.type === "3D" && bestModel.format === "html") {
				trackEvent(
					"model_3d_opened",
					buildModelAnalyticsPayload("3D", filename, {
						model_format: "html",
						processed_model: true,
					})
				);

				setLoading(true);
				const res = await processModelFile({
					filename,
					machinename: machineName,
					currency: selectedSiteCurrency.toUpperCase(),
					loggedinUser: user.user_id,
					ecommachineid: machineid,
					baseColor: theme.palette.primary.main,
					selectedSite,
					site_id: selectedSiteData?.site_id || "",
					cus_id: selectedSiteData?.cus_id || selectedSiteData?.customer_id || "",
					machinenumber: machineNumber,
					...(bestModel.engine === "inhouse" ? { engine: "inhouse" } : {}),
				});

				if (res?.data?.url) {
					if (bestModel.engine === "inhouse" || res.data.kind === "inhouse") {
						const modelUrl = SERVER_BASE_URL + res.data.url;
						if (!(await verifyResourceExists(modelUrl))) {
							showError();
							return;
						}
						navigate("/html-model-viewer/" + machineid + "/" + extractFilename(res.data.url), {
							state: { modelUrl, fromPath: returnPath, machineName, machineNumber, kind: "inhouse" },
						});
					} else {
						await openUrlIfExists(SERVER_BASE_URL + res.data.url);
					}
				} else {
					showError();
				}
				return;
			}
		} catch {
			showError();
		} finally {
			setLoading(false);
		}
	};
	if (!forcedModel && !hasDirectMachineModelsFromModelData(modelData)) return null;
	if (!bestModel) return null;

	return (
		<>
			<Tooltip title={bestModel.type === "3D" ? "View 3D Model" : t('MACHINE_MANAGEMENT.VIEW_2D_DRAWING')}>
				<IconButton
					disabled={loading}
					onClick={(e) => {
						e.stopPropagation();
						shouldShowMultipleSelector ? setModalOpen(true) : handleOpen();
					}}
					sx={{ width: 40, height: 40 }}
				>
					{loading ? (
						<CircularProgress size={20} />
					) : (
						bestModel.type === "3D" ? <ModelIcon /> : <ModelPDFIcon />
					)}
				</IconButton>
			</Tooltip>

			{shouldShowMultipleSelector && (
				<MultipleModelViewer
					show={modalOpen}
					setShow={setModalOpen}
					machine_number={machineNumber}
					machine_name={machineName}
					productNumbers={productNumbers}
					machine_id={machineid}
					machine_product_name={machineProductName}
					modelData={modelData}
				/>
			)}
		</>
	);
};

export default ModelButton;
